import type { Raw } from 'vue';
import type { AnyItem, CompositePrimaryKey, GlobalCollectionId, GlobalPrimitiveFieldId, QueryCapability, QuerySchemaCollection, QuerySchemaRelationWrite, QuerySchemaWrite, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { markRaw } from 'vue';
import { isEmptyKey, isQueryCapabilityAvailable, mapQueryCapability, queryCapabilityAllows, QueryCapabilityKinds, supportedSurface, UNSUPPORTED_CAPABILITY } from '#layers/data-model/public';

export const FormWriteOrigins = {
	RootCreate: 'root-create',
	RootUpdate: 'root-update',
	NestedCreate: 'nested-create',
	NestedUpdate: 'nested-update',
	View: 'view',
} as const;

type Origins = typeof FormWriteOrigins;
type FormWriteOrigin = Origins[keyof Origins];

const createsItem = {
	[FormWriteOrigins.RootCreate]: true,
	[FormWriteOrigins.RootUpdate]: false,
	[FormWriteOrigins.NestedCreate]: true,
	[FormWriteOrigins.NestedUpdate]: false,
	[FormWriteOrigins.View]: false,
} as const satisfies Record<FormWriteOrigin, boolean>;

type FormWriteTarget =
	| { readonly origin: Origins['RootCreate'] | Origins['NestedCreate'] }
	| { readonly origin: Origins['RootUpdate'] | Origins['NestedUpdate'] | Origins['View']; readonly key: CompositePrimaryKey };

/**
 * The operation a form submits through, and what that operation accepts.
 *
 * A root form writes through its own collection's create or by-key update. A drawer opened from a
 * relation writes through its parent's nested create or update on that relation, so its context is
 * derived from the parent's, never from the target collection's own operations. A view shows an
 * existing item and writes through nothing.
 *
 * Instances are raw: reactive unwrapping would strip them to a structural copy without the private
 * members, so factories return `Raw<FormWriteContext>` and the constructor marks each instance.
 */
export class FormWriteContext {
	private constructor(
		readonly collectionId: GlobalCollectionId | undefined,
		readonly target: FormWriteTarget,
		private readonly write: QueryCapability<QuerySchemaWrite>,
	) {
		markRaw(this);
	}

	/** A form without a key creates; a form with one updates that item by key. */
	static root(collectionId: GlobalCollectionId | undefined, api: QueryCapability<QuerySchemaCollection>, key: CompositePrimaryKey | null): Raw<FormWriteContext> {
		if (key === null) {
			return new FormWriteContext(collectionId, { origin: FormWriteOrigins.RootCreate }, mapQueryCapability(api, (api) => api.create()));
		}

		const updateOne = isEmptyKey(key) ? UNSUPPORTED_CAPABILITY : mapQueryCapability(api, (api) => api.updateOne());
		return new FormWriteContext(collectionId, { origin: FormWriteOrigins.RootUpdate, key }, updateOne);
	}

	/** A form that shows the item `key` and offers no write. */
	static view(collectionId: GlobalCollectionId, key: CompositePrimaryKey): Raw<FormWriteContext> {
		return new FormWriteContext(collectionId, { origin: FormWriteOrigins.View, key }, UNSUPPORTED_CAPABILITY);
	}

	get isCreate(): boolean {
		return createsItem[this.target.origin];
	}

	get isView(): boolean {
		return this.target.origin === FormWriteOrigins.View;
	}

	get available(): boolean {
		return isQueryCapabilityAvailable(this.write);
	}

	allowsPrimitiveField(id: GlobalPrimitiveFieldId): boolean {
		return queryCapabilityAllows(this.write, (write) => write.primitiveFieldById(id) !== undefined);
	}

	/**
	 * Whether the form marks `field` required. A create requires what its input may not omit, an update
	 * what it may not clear. An ungated write, which has no input to consult, requires non-nullable fields.
	 */
	requiresPrimitiveField(field: ResolvedMonospacePrimitiveField): boolean {
		const write = this.write;

		switch (write.kind) {
			case QueryCapabilityKinds.Ungated:
				return !field.isNullable;
			case QueryCapabilityKinds.Unavailable:
			case QueryCapabilityKinds.Unsupported:
				return false;
			case QueryCapabilityKinds.Supported: {
				const input = write.value.primitiveFieldById(field.id);
				if (!input) return false;
				return this.isCreate ? !input.optional : !input.nullable;
			}
			default:
				write satisfies never;
				return false;
		}
	}

	/**
	 * Whether the form marks the relation `field` required: a create input that may not omit it. An
	 * update's relation input is a list of operations that states no nullability, so it requires none.
	 */
	requiresRelation(field: ResolvedMonospaceSingleRelationField): boolean {
		if (!this.isCreate) return false;
		const relation = supportedSurface(this.relation(field));
		return relation !== undefined && !relation.optional;
	}

	/** The nested operations this write accepts on one of its collection's relation fields. */
	relation(field: ResolvedMonospaceSingleRelationField): QueryCapability<QuerySchemaRelationWrite> {
		if (field.collection?.id !== this.collectionId) return UNSUPPORTED_CAPABILITY;
		// The engine does not know Studio-only relations; they follow the form's own availability.
		if (field.isStudioOnly && this.available) return { kind: QueryCapabilityKinds.Ungated };
		return mapQueryCapability(this.write, (write) => write.relationFieldById(field.id));
	}

	/**
	 * The part of `item`, a row of `collection`, this write's payload accepts.
	 *
	 * The engine rejects a whole write over one field its input does not offer, such as the key a nested update
	 * identifies its row by or a field only reads return, so every other field is dropped. Symbol-keyed
	 * bookkeeping is kept. An ungated write has no input to consult and keeps the item whole.
	 */
	projectPayload(item: AnyItem, collection: ResolvedMonospaceCollection): AnyItem {
		if (this.write.kind === QueryCapabilityKinds.Ungated) return item;

		const accepted = [
			...collection.primitiveFields.filter((field) => this.allowsPrimitiveField(field.id)),
			...collection.relationFields.filter((field) => isQueryCapabilityAvailable(this.relation(field))),
		];
		const payload: AnyItem = {};

		for (const { apiName } of accepted) {
			if (Object.hasOwn(item, apiName)) payload[apiName] = item[apiName];
		}

		for (const symbol of Object.getOwnPropertySymbols(item)) {
			payload[symbol] = item[symbol];
		}

		return payload;
	}

	/** The context for a drawer creating an item on `field` as part of this write. */
	createRelated(field: ResolvedMonospaceSingleRelationField): Raw<FormWriteContext> | undefined {
		const create = mapQueryCapability(this.relation(field), (relation) => relation.create());
		return this.nested(field, { origin: FormWriteOrigins.NestedCreate }, create);
	}

	/** The context for a drawer updating the item `key` on `field` as part of this write. */
	updateRelated(field: ResolvedMonospaceSingleRelationField, key: CompositePrimaryKey): Raw<FormWriteContext> | undefined {
		if (isEmptyKey(key)) return;
		const update = mapQueryCapability(this.relation(field), (relation) => relation.update());
		return this.nested(field, { origin: FormWriteOrigins.NestedUpdate, key }, update);
	}

	private nested(field: ResolvedMonospaceSingleRelationField, target: FormWriteTarget, write: QueryCapability<QuerySchemaWrite>): Raw<FormWriteContext> | undefined {
		if (!isQueryCapabilityAvailable(write)) return;
		return new FormWriteContext(field.oppositeCollectionId, target, write);
	}
}

export const RelationEditKinds = {
	Created: 'created',
	Connected: 'connected',
	Persisted: 'persisted',
} as const;

type EditKinds = typeof RelationEditKinds;

/** A related item a drawer edits: a pending create has no key yet; connected and persisted items do. */
export type RelationEditState =
	| { readonly kind: EditKinds['Created'] }
	| { readonly kind: EditKinds['Connected'] | EditKinds['Persisted']; readonly key: CompositePrimaryKey };

/** A persisted M2M target is edited through its junction row, so it also carries the junction's keys. */
export type M2MEditState =
	| { readonly kind: EditKinds['Created'] }
	| { readonly kind: EditKinds['Connected']; readonly key: CompositePrimaryKey }
	| { readonly kind: EditKinds['Persisted']; readonly key: CompositePrimaryKey; readonly junctionKey: CompositePrimaryKey; readonly referenceKey: CompositePrimaryKey };
